import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {Menu, X, Globe, ShoppingBag} from 'lucide-react'
import { useLang } from "@/context/LanguageContext"
import { useCart } from "@/context/CartContext"

export default function Navbar() {
  const { lang, toggleLang, t } = useLang()
  const { totalItems, setIsOpen: setCartOpen } = useCart()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [activeSection, setActiveSection] = useState("")

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 40)
    window.addEventListener("scroll", handler, { passive: true })
    return () => window.removeEventListener("scroll", handler)
  }, [])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the most visible section
        let maxRatio = 0
        let mostVisibleSection = ""
        
        entries.forEach((entry) => {
          if (entry.intersectionRatio > maxRatio) {
            maxRatio = entry.intersectionRatio
            mostVisibleSection = entry.target.id
          }
        })
        
        if (mostVisibleSection) {
          setActiveSection(mostVisibleSection)
        }
      },
      { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0] }
    )

    const sections = ["hero", "catalogue", "savoir-faire", "faq", "acheter", "contact"]
    sections.forEach((id) => {
      const element = document.getElementById(id)
      if (element) observer.observe(element)
    })

    return () => observer.disconnect()
  }, [])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })
    setMobileOpen(false)
  }

  const navLinks = [
    { label: t("Produit", "Product"), id: "savoir-faire" },
    { label: "Collections", id: "catalogue" },
    { label: t("Acheter", "Buy"), id: "acheter" },
    { label: "FAQ", id: "faq" },
    { label: "Contact", id: "contact" },
  ]

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl border-b"
          : "bg-transparent"
      }`}
      style={{
        backgroundColor: scrolled ? "rgba(248, 242, 235, 0.92)" : "transparent",
        borderColor: scrolled ? "rgba(130, 57, 31, 0.1)" : "transparent",
      }}
    >
      <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
        <button onClick={() => scrollTo("hero")} className="flex items-center gap-3 group">
          <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="14" cy="14" r="13" stroke={scrolled ? "#E1593F" : "rgba(255,255,255,0.8)"} strokeWidth="1.5" />
            <circle cx="14" cy="14" r="10" fill={scrolled ? "#E1593F" : "rgba(255,255,255,0.8)"} />
          </svg>
          <span
            className="text-base tracking-[4px] uppercase"
            style={{
              fontFamily: "Segoe UI Light, Segoe UI, system-ui, sans-serif",
              fontWeight: 300,
              color: scrolled ? "#0D0C0B" : "rgba(255,255,255,0.9)",
            }}
          >
            CLIPS'ON
          </span>
        </button>

        <div className="hidden md:flex items-center gap-10">
          {navLinks.map(link => {
            const isActive = activeSection === link.id
            return (
              <button
                key={link.id}
                onClick={() => scrollTo(link.id)}
                className="tracking-[0.15em] uppercase transition-all duration-300 relative group"
                style={{ 
                  color: scrolled ? "rgba(13, 12, 11, 0.5)" : "rgba(255,255,255,0.6)",
                  fontSize: "15px",
                  fontWeight: 400,
                  paddingBottom: "6px"
                }}
                onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
                onMouseLeave={e => (e.currentTarget.style.color = scrolled ? "rgba(13, 12, 11, 0.5)" : "rgba(255,255,255,0.6)")}
              >
                {link.label}
                <span 
                  className="absolute left-0 right-0 h-[1px] transition-opacity duration-300"
                  style={{
                    bottom: 0,
                    backgroundColor: "#E1593F",
                    opacity: isActive ? 1 : 0
                  }}
                />
                <span 
                  className="absolute left-0 right-0 h-[1px] opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{
                    bottom: 0,
                    backgroundColor: "#E1593F"
                  }}
                />
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-4">
          {/* Language toggle */}
          <button
            onClick={toggleLang}
            className="flex items-center gap-1.5 text-sm tracking-wider uppercase transition-colors duration-300"
            style={{ color: scrolled ? "rgba(13, 12, 11, 0.5)" : "rgba(255,255,255,0.6)" }}
            onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
            onMouseLeave={e => (e.currentTarget.style.color = scrolled ? "rgba(13, 12, 11, 0.5)" : "rgba(255,255,255,0.6)")}
          >
            <Globe size={15} strokeWidth={1.5} />
            {lang === "fr" ? "EN" : "FR"}
          </button>

          <button
            onClick={() => setCartOpen(true)}
            className="relative transition-colors"
            style={{ color: scrolled ? "#0D0C0B" : "rgba(255,255,255,0.7)" }}
            onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
            onMouseLeave={e => (e.currentTarget.style.color = scrolled ? "#0D0C0B" : "rgba(255,255,255,0.7)")}
            aria-label={t("Panier", "Cart")}
          >
            <ShoppingBag size={20} strokeWidth={1.5} />
            {totalItems > 0 ? (
              <span
                className="absolute -top-1.5 -right-1.5 flex items-center justify-center text-[10px] rounded-full text-white"
                style={{ backgroundColor: "#E1593F", width: "16px", height: "16px" }}
              >
                {totalItems}
              </span>
            ) : null}
          </button>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden transition-colors"
            style={{ color: scrolled ? "#0D0C0B" : "rgba(255,255,255,0.7)" }}
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden backdrop-blur-xl overflow-hidden"
            style={{ backgroundColor: "rgba(248, 242, 235, 0.97)", borderTop: "1px solid rgba(130, 57, 31, 0.08)" }}
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {navLinks.map(link => {
                const isActive = activeSection === link.id
                return (
                  <button
                    key={link.id}
                    onClick={() => scrollTo(link.id)}
                    className="tracking-[0.15em] uppercase text-left py-2 transition-colors relative"
                    style={{ 
                      color: "rgba(13, 12, 11, 0.5)",
                      fontSize: "15px",
                      fontWeight: 400,
                      paddingBottom: "8px"
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = "#E1593F")}
                    onMouseLeave={e => (e.currentTarget.style.color = "rgba(13, 12, 11, 0.5)")}
                  >
                    {link.label}
                    {isActive && (
                      <span 
                        className="absolute left-0 h-[1px] w-8"
                        style={{
                          bottom: "2px",
                          backgroundColor: "#E1593F"
                        }}
                      />
                    )}
                  </button>
                )
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  )
}
